/**
 * Counted / Received for one product, from the dashboard.
 *
 * Counted writes an ABSOLUTE `count_adjust`, which becomes the new baseline
 * and discards every delta before it — a physical count already accounts for
 * whatever happened earlier. That makes it the one action here that can erase
 * a day of sales from the on-hand figure, so it shows the variance before
 * confirming.
 *
 * Received writes a `purchase` delta and needs no such care: adding stock you
 * physically received cannot hide a discrepancy. It is kept for products
 * only — an ingredient arrives on a goods-received document, which records
 * the supplier and the cost as well as the quantity.
 *
 * Waste writes a negative delta with a reason.
 *
 * The same modal serves products and ingredients. An ingredient carries a
 * unit (kg, l, Item) and accepts decimals, because 0.75 kg is a real amount;
 * a product counts in whole units.
 *
 * For a product tracked per variant the modal is opened with the variant
 * FIXED (`variantId`): the event carries it as optionId and moves only that
 * variant's level. GRN/variants spec section 5.4.
 */
import {useState} from 'react';
import Modal from 'react-modal';
import {AiOutlineClose} from 'react-icons/ai';
import StockEventRepository from '../../../internals/repository/StockEventRepository';

export type AdjustMode = 'count' | 'receive' | 'waste';

export const WASTE_REASONS = [
  {value: 'spoiled', label: 'Spoiled'},
  {value: 'damaged', label: 'Damaged'},
  {value: 'dropped', label: 'Dropped'},
  {value: 'expired', label: 'Expired'},
  {value: 'staff_meal', label: 'Staff meal'},
  {value: 'other', label: 'Other'},
];

interface Props {
  shopId: string;
  /** A product line, or an ingredient line — one of the two. */
  productId?: string;
  stockId?: string;
  /** The one variant this adjusts, for a product tracked per variant. */
  variantId?: string | null;
  variantLabel?: string | null;
  productName: string;
  onHand: number;
  /** Ingredient unit (kg, l, Item). Absent for a product, which counts in units. */
  unit?: string;
  /** Smallest entry step. 0.01 for kg and l, 1 for pieces. */
  step?: number;
  mode: AdjustMode;
  onClose: () => void;
  /**
   * Called once the event is accepted. `detail` says what was recorded, so a
   * screen can show the new figure before it refetches.
   */
  onDone: (message: string, detail?: {mode: AdjustMode; quantity: number}) => void;
}

const StockAdjustModal = ({
  shopId,
  productId,
  stockId,
  variantId,
  variantLabel,
  productName: baseName,
  onHand,
  unit,
  step = 1,
  mode,
  onClose,
  onDone,
}: Props) => {
  const productName = variantLabel ? `${baseName} · ${variantLabel}` : baseName;
  const isCount = mode === 'count';
  const isWaste = mode === 'waste';
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState(WASTE_REASONS[0].value);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const unitLabel = unit ? ` ${unit}` : '';

  const parsed = Number(qty);
  const valid = qty.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
  const variance = valid ? Number((parsed - onHand).toFixed(3)) : 0;

  const varianceClass = () => {
    if (variance === 0) return 'text-text2';
    return variance < 0 ? 'text-red-600' : 'text-emerald-600';
  };

  const submit = async () => {
    setError('');
    if (!valid) {
      setError(
        isCount
          ? 'Enter the counted quantity'
          : `Enter the ${isWaste ? 'wasted' : 'received'} quantity`,
      );
      return;
    }
    if (!isCount && parsed === 0) {
      setError(`${isWaste ? 'Wasted' : 'Received'} quantity must be greater than 0`);
      return;
    }

    setBusy(true);
    try {
      const res = await StockEventRepository.recordEvent(shopId, {
        ...(stockId ? {stockId} : {productId}),
        ...(variantId && !stockId ? {variantId} : {}),
        // eslint-disable-next-line no-nested-ternary
        type: isCount ? 'count_adjust' : isWaste ? 'waste' : 'purchase',
        quantity: parsed,
        ...(isWaste ? {reason} : {}),
      });
      // The route reports per-event outcomes rather than failing the call, so
      // a rejected event arrives as a 200 with failedCount — say so plainly
      // instead of claiming success.
      if (res.failedCount > 0) {
        setError(res.results?.[0]?.reason || 'The server rejected this event');
        return;
      }
      // eslint-disable-next-line no-nested-ternary
      onDone(
        isCount
          ? `${productName} counted at ${parsed}${unitLabel}`
          : isWaste
            ? `Wasted ${parsed}${unitLabel} of ${productName}`
            : `Received ${parsed}${unitLabel} × ${productName}`,
        {mode, quantity: parsed},
      );
    } catch (e: any) {
      setError(e.message || 'Could not record the event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      ariaHideApp={false}
      className="absolute left-1/2 top-1/2 w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface p-0 shadow-xl outline-none"
      overlayClassName="fixed inset-0 bg-black/40 z-[1000]"
    >
      <div className="flex items-center justify-between border-b border-borderc px-5 py-4">
        <h2 className="text-base font-semibold text-text1">
          {/* eslint-disable-next-line no-nested-ternary */}
          {isCount ? 'Count stock' : isWaste ? 'Record waste' : 'Receive stock'}
        </h2>
        <button type="button" onClick={onClose} className="text-text3 hover:text-text1">
          <AiOutlineClose size={16} />
        </button>
      </div>

      <div className="flex flex-col gap-4 p-5">
        <div>
          <p className="text-sm font-medium text-text1">{productName}</p>
          <p className="mt-0.5 font-mono text-xs text-text3">
            {onHand}{unitLabel} on hand
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-text3">
            {/* eslint-disable-next-line no-nested-ternary */}
            {isCount ? 'Counted quantity' : isWaste ? 'Wasted quantity' : 'Received quantity'}
            {unit ? ` (${unit})` : ''}
          </span>
          <input
            type="number"
            min="0"
            step={step}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="0"
            className="nl-input text-right font-mono"
          />
        </div>

        {isWaste && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-text3">Reason</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="nl-select"
            >
              {WASTE_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {isCount && valid && (
          <div className="flex items-center justify-between rounded-lg border border-borderc bg-surface2 px-3 py-2.5">
            <span className="text-xs text-text2">Variance</span>
            <span className={`font-mono text-sm font-semibold ${varianceClass()}`}>
              {variance > 0 ? '+' : ''}
              {variance}
            </span>
          </div>
        )}

        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="nl-btn nl-btn--secondary flex-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="nl-btn nl-btn--primary flex-1"
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {busy ? 'Saving…' : isCount ? 'Save count' : isWaste ? 'Record waste' : 'Receive'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StockAdjustModal;
